// PhotographyIndustries.js - Photography Industries & Clients section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed Industries & Clients section with interactive cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the industries section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The industries elements
 */
export const createPhotographyIndustriesElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const industriesSectionClass = 'photography-industries-section';
  const industriesContainerClass = 'photography-industries-container';
  const industriesHeaderClass = 'photography-industries-header';
  const industriesTitleClass = 'photography-industries-title';
  const industriesSubtitleClass = 'photography-industries-subtitle';
  const industriesGridClass = 'photography-industries-grid';
  const industryCardClass = 'photography-industry-card';
  const industryImageClass = 'photography-industry-image';
  const industryOverlayClass = 'photography-industry-overlay';
  const industryContentClass = 'photography-industry-content';
  const industryIconClass = 'photography-industry-icon';
  const industryNameClass = 'photography-industry-name';
  const industryDescClass = 'photography-industry-description';
  const industryStatsClass = 'photography-industry-stats';
  const industryStatClass = 'photography-industry-stat';
  const industryStatNumberClass = 'photography-industry-stat-number';
  const industryStatLabelClass = 'photography-industry-stat-label';
  const industryTagsClass = 'photography-industry-tags';
  const industryTagClass = 'photography-industry-tag';
  const industriesPatternOverlayClass = 'photography-industries-pattern-overlay';

  // Desktop Styles (default) - Photography theme with elegant dark design
  const industriesSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#1a1a1a',
    background: `
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.06) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.04) 0%, transparent 60%),
      linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 50%, #1a1a1a 100%)
    `,
    color: '#f5f5f5',
    width: '100%',
    overflow: 'hidden'
  };

  const industriesPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      linear-gradient(45deg, rgba(212, 175, 55, 0.02) 1px, transparent 1px),
      linear-gradient(-45deg, rgba(212, 175, 55, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px',
    zIndex: '0',
    opacity: '0.3'
  };

  const industriesContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const industriesHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const industriesTitleStyles = {
    fontSize: '52px',
    fontWeight: '300',
    color: '#f5f5f5',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const industriesSubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(245, 245, 245, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const industriesGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '40px',
    marginTop: '60px'
  };

  const industryCardStyles = {
    position: 'relative',
    height: '450px',
    borderRadius: '0',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(26, 26, 26, 0.8)',
    border: '1px solid rgba(212, 175, 55, 0.1)'
  };

  // Hover styles for industry card
  const industryCardHoverStyles = {
    transform: 'translateY(-12px) scale(1.02)',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    boxShadow: `
      0 25px 50px rgba(0, 0, 0, 0.5),
      0 0 30px rgba(212, 175, 55, 0.2)
    `
  };

  const industryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'grayscale(20%) brightness(0.7)'
  };

  // Hover styles for industry image
  const industryImageHoverStyles = {
    filter: 'grayscale(0%) brightness(0.9)',
    transform: 'scale(1.05)'
  };

  const industryOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(212, 175, 55, 0.1) 0%, 
        rgba(26, 26, 26, 0.7) 40%, 
        rgba(15, 15, 15, 0.9) 100%
      )
    `,
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  // Hover styles for industry overlay
  const industryOverlayHoverStyles = {
    background: `
      linear-gradient(135deg, 
        rgba(212, 175, 55, 0.2) 0%, 
        rgba(26, 26, 26, 0.6) 40%, 
        rgba(15, 15, 15, 0.8) 100%
      )
    `
  };

  const industryContentStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '40px 30px',
    zIndex: '2',
    transform: 'translateY(0)',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Hover styles for industry content
  const industryContentHoverStyles = {
    transform: 'translateY(-10px)'
  };

  const industryIconStyles = {
    fontSize: '48px',
    color: '#d4af37',
    marginBottom: '20px',
    display: 'block',
    transition: 'all 0.4s ease',
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)'
  };

  // Hover styles for industry icon
  const industryIconHoverStyles = {
    fontSize: '52px',
    textShadow: '0 4px 20px rgba(212, 175, 55, 0.5)',
    transform: 'scale(1.1)'
  };

  const industryNameStyles = {
    fontSize: '28px',
    fontWeight: '400',
    color: '#f5f5f5',
    marginBottom: '16px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const industryDescStyles = {
    fontSize: '15px',
    color: 'rgba(245, 245, 245, 0.9)',
    lineHeight: '1.6',
    marginBottom: '25px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300',
    opacity: '0.9'
  };

  const industryStatsStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '20px'
  };

  const industryStatStyles = {
    textAlign: 'center',
    flex: '1'
  };

  const industryStatNumberStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#d4af37',
    display: 'block',
    fontFamily: '"Inter", sans-serif',
    marginBottom: '4px'
  };

  const industryStatLabelStyles = {
    fontSize: '12px',
    color: 'rgba(245, 245, 245, 0.7)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const industryTagsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '20px'
  };

  const industryTagStyles = {
    padding: '6px 12px',
    background: 'rgba(212, 175, 55, 0.1)',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    borderRadius: '0',
    fontSize: '11px',
    color: '#d4af37',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease'
  };

  // Hover styles for industry tag
  const industryTagHoverStyles = {
    background: 'rgba(212, 175, 55, 0.2)',
    border: '1px solid rgba(212, 175, 55, 0.5)',
    color: '#f5f5f5',
    transform: 'translateY(-2px)'
  };

  // Tablet Styles
  const industriesTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const industriesTitleTabletStyles = {
    fontSize: '44px'
  };

  const industriesSubtitleTabletStyles = {
    fontSize: '18px'
  };

  const industriesGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '30px'
  };

  const industryCardTabletStyles = {
    height: '400px'
  };

  const industryContentTabletStyles = {
    padding: '35px 25px'
  };

  // Mobile Styles
  const industriesMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const industriesTitleMobileStyles = {
    fontSize: '36px'
  };

  const industriesSubtitleMobileStyles = {
    fontSize: '16px'
  };

  const industriesGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '25px'
  };

  const industryCardMobileStyles = {
    height: '350px'
  };

  const industryContentMobileStyles = {
    padding: '30px 20px'
  };

  const industryIconMobileStyles = {
    fontSize: '40px'
  };

  const industryNameMobileStyles = {
    fontSize: '24px'
  };

  const industryStatsMobileStyles = {
    gap: '15px'
  };

  const industryStatNumberMobileStyles = {
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(industriesSectionClass, industriesSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesPatternOverlayClass, industriesPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesContainerClass, industriesContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesHeaderClass, industriesHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTitleClass, industriesTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesSubtitleClass, industriesSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesGridClass, industriesGridStyles);
    newRegistry[DEVICES.DESKTOP].set(industryCardClass, industryCardStyles);
    newRegistry[DEVICES.DESKTOP].set(industryImageClass, industryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(industryOverlayClass, industryOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(industryContentClass, industryContentStyles);
    newRegistry[DEVICES.DESKTOP].set(industryIconClass, industryIconStyles);
    newRegistry[DEVICES.DESKTOP].set(industryNameClass, industryNameStyles);
    newRegistry[DEVICES.DESKTOP].set(industryDescClass, industryDescStyles);
    newRegistry[DEVICES.DESKTOP].set(industryStatsClass, industryStatsStyles);
    newRegistry[DEVICES.DESKTOP].set(industryStatClass, industryStatStyles);
    newRegistry[DEVICES.DESKTOP].set(industryStatNumberClass, industryStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(industryStatLabelClass, industryStatLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(industryTagsClass, industryTagsStyles);
    newRegistry[DEVICES.DESKTOP].set(industryTagClass, industryTagStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${industryCardClass}--hover`, industryCardHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${industryImageClass}--hover`, industryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${industryOverlayClass}--hover`, industryOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${industryContentClass}--hover`, industryContentHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${industryIconClass}--hover`, industryIconHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${industryTagClass}--hover`, industryTagHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(industriesSectionClass, industriesTabletStyles);
    newRegistry[DEVICES.TABLET].set(industriesTitleClass, industriesTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(industriesSubtitleClass, industriesSubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(industriesGridClass, industriesGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(industryCardClass, industryCardTabletStyles);
    newRegistry[DEVICES.TABLET].set(industryContentClass, industryContentTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(industriesSectionClass, industriesMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industriesTitleClass, industriesTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industriesSubtitleClass, industriesSubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industriesGridClass, industriesGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industryCardClass, industryCardMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industryContentClass, industryContentMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industryIconClass, industryIconMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industryNameClass, industryNameMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industryStatsClass, industryStatsMobileStyles);
    newRegistry[DEVICES.MOBILE].set(industryStatNumberClass, industryStatNumberMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    industriesSectionClass,
    industriesPatternOverlayClass,
    industriesContainerClass,
    industriesHeaderClass,
    industriesTitleClass,
    industriesSubtitleClass,
    industriesGridClass,
    industryCardClass,
    industryImageClass,
    industryOverlayClass,
    industryContentClass,
    industryIconClass,
    industryNameClass,
    industryDescClass,
    industryStatsClass,
    industryStatClass,
    industryStatNumberClass,
    industryStatLabelClass,
    industryTagsClass,
    industryTagClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Industries data
  const industries = [
    {
      icon: '💼',
      name: 'Corporate & Business',
      description: 'Professional headshots, corporate events, and brand photography that elevates your business presence.',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop&crop=center',
      stats: {
        projects: '150+',
        clients: '80+',
        years: '5+'
      },
      tags: ['Headshots', 'Events', 'Branding', 'Team Photos']
    },
    {
      icon: '💕',
      name: 'Weddings & Events',
      description: 'Capturing life\'s most precious moments with artistic storytelling and timeless elegance.',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop&crop=center',
      stats: {
        weddings: '200+',
        couples: '300+',
        years: '8+'
      },
      tags: ['Weddings', 'Engagements', 'Celebrations', 'Portraits']
    },
    {
      icon: '🏠',
      name: 'Real Estate',
      description: 'Stunning property photography that showcases homes and commercial spaces in their best light.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop&crop=center',
      stats: {
        properties: '500+',
        agents: '50+',
        sales: '95%'
      },
      tags: ['Interiors', 'Exteriors', 'Virtual Tours', 'Staging']
    },
    {
      icon: '🍽️',
      name: 'Food & Hospitality',
      description: 'Mouth-watering food photography and restaurant branding that makes your cuisine irresistible.',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop&crop=center',
      stats: {
        restaurants: '75+',
        dishes: '1000+',
        campaigns: '120+'
      },
      tags: ['Menu Photography', 'Restaurant Branding', 'Food Styling', 'Ambiance']
    },
    {
      icon: '👗',
      name: 'Fashion & Beauty',
      description: 'High-fashion photography and beauty campaigns that capture style, elegance, and personality.',
      image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&h=400&fit=crop&crop=center',
      stats: {
        models: '100+',
        brands: '40+',
        shoots: '250+'
      },
      tags: ['Fashion Shoots', 'Beauty Campaigns', 'Lookbooks', 'Editorial']
    },
    {
      icon: '🎨',
      name: 'Creative & Artistic',
      description: 'Conceptual and artistic photography for galleries, exhibitions, and creative projects.',
      image: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=400&fit=crop&crop=center',
      stats: {
        exhibitions: '25+',
        artworks: '300+',
        galleries: '15+'
      },
      tags: ['Fine Art', 'Conceptual', 'Exhibitions', 'Creative Direction']
    }
  ];

  // Create industry card elements
  let currentId = gridId + 1;
  const industryCardElements = [];

  industries.forEach((industry, index) => {
    const cardId = currentId++;
    const imageId = currentId++;
    const overlayId = currentId++;
    const contentId = currentId++;
    const iconId = currentId++;
    const nameId = currentId++;
    const descId = currentId++;
    const statsId = currentId++;
    const tagsId = currentId++;

    // Create stat elements
    const statElements = [];
    const statIds = [];
    Object.entries(industry.stats).forEach(([key, value], statIndex) => {
      const statId = currentId++;
      const statNumberId = currentId++;
      const statLabelId = currentId++;
      
      statIds.push(statId);
      
      statElements.push(
        {
          id: statId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'div',
          name: `Industry Stat ${statIndex + 1}`,
          parentId: statsId,
          hierarchyName: getHierarchyName('div'),
          className: industryStatClass,
          styles: {},
          children: [statNumberId, statLabelId],
          text: '',
          attributes: {
            'data-element': 'industry-stat'
          }
        },
        {
          id: statNumberId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'span',
          name: `Stat Number ${statIndex + 1}`,
          parentId: statId,
          hierarchyName: getHierarchyName('span'),
          className: industryStatNumberClass,
          styles: {},
          children: [],
          text: value,
          attributes: {
            'data-element': 'stat-number'
          }
        },
        {
          id: statLabelId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'span',
          name: `Stat Label ${statIndex + 1}`,
          parentId: statId,
          hierarchyName: getHierarchyName('span'),
          className: industryStatLabelClass,
          styles: {},
          children: [],
          text: key,
          attributes: {
            'data-element': 'stat-label'
          }
        }
      );
    });

    // Create tag elements
    const tagElements = [];
    const tagIds = [];
    industry.tags.forEach((tag, tagIndex) => {
      const tagId = currentId++;
      tagIds.push(tagId);
      tagElements.push({
        id: tagId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Industry Tag ${tagIndex + 1}`,
        parentId: tagsId,
        hierarchyName: getHierarchyName('span'),
        className: industryTagClass,
        styles: {},
        children: [],
        text: tag,
        attributes: {
          'data-element': 'industry-tag'
        }
      });
    });

    // Add card elements
    industryCardElements.push(
      // Industry Card
      {
        id: cardId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Industry Card ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: industryCardClass,
        styles: {},
        children: [imageId, overlayId, contentId],
        text: '',
        attributes: {
          'data-element': 'industry-card',
          'data-industry': industry.name.toLowerCase().replace(/\s+/g, '-')
        }
      },
      // Industry Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Industry Image ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('image'),
        className: industryImageClass,
        styles: {},
        src: industry.image,
        attributes: {
          'data-element': 'industry-image',
          'alt': `${industry.name} Photography`
        }
      },
      // Industry Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Industry Overlay ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('div'),
        className: industryOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'industry-overlay'
        }
      },
      // Industry Content
      {
        id: contentId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Industry Content ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('div'),
        className: industryContentClass,
        styles: {},
        children: [iconId, nameId, descId, statsId, tagsId],
        text: '',
        attributes: {
          'data-element': 'industry-content'
        }
      },
      // Industry Icon
      {
        id: iconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Industry Icon ${index + 1}`,
        parentId: contentId,
        hierarchyName: getHierarchyName('span'),
        className: industryIconClass,
        styles: {},
        children: [],
        text: industry.icon,
        attributes: {
          'data-element': 'industry-icon'
        }
      },
      // Industry Name
      {
        id: nameId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h3',
        name: `Industry Name ${index + 1}`,
        parentId: contentId,
        hierarchyName: getHierarchyName('h3'),
        className: industryNameClass,
        styles: {},
        children: [],
        text: industry.name,
        attributes: {
          'data-element': 'industry-name'
        }
      },
      // Industry Description
      {
        id: descId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: `Industry Description ${index + 1}`,
        parentId: contentId,
        hierarchyName: getHierarchyName('p'),
        className: industryDescClass,
        styles: {},
        children: [],
        text: industry.description,
        attributes: {
          'data-element': 'industry-description'
        }
      },
      // Industry Stats
      {
        id: statsId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Industry Stats ${index + 1}`,
        parentId: contentId,
        hierarchyName: getHierarchyName('div'),
        className: industryStatsClass,
        styles: {},
        children: statIds,
        text: '',
        attributes: {
          'data-element': 'industry-stats'
        }
      },
      // Industry Tags
      {
        id: tagsId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Industry Tags ${index + 1}`,
        parentId: contentId,
        hierarchyName: getHierarchyName('div'),
        className: industryTagsClass,
        styles: {},
        children: tagIds,
        text: '',
        attributes: {
          'data-element': 'industry-tags'
        }
      }
    );

    // Add stat and tag elements
    industryCardElements.push(...statElements, ...tagElements);
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography Industries Section',
      parentId: null,
      className: industriesSectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'industries-section',
        'data-component': 'section',
        'data-section-type': 'industries',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: industriesPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'industries-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: industriesContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'industries-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: industriesHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'industries-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Industries Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: industriesTitleClass,
      styles: {},
      children: [],
      text: 'Industries & Clients We Serve',
      attributes: {
        'data-element': 'industries-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Industries Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: industriesSubtitleClass,
      styles: {},
      children: [],
      text: 'From intimate portraits to large-scale commercial projects, we bring creativity and professionalism to every industry we serve.',
      attributes: {
        'data-element': 'industries-subtitle'
      }
    },
    // Industries Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: industriesGridClass,
      styles: {},
      children: industryCardElements.filter(el => el.parentId === gridId).map(el => el.id),
      text: '',
      attributes: {
        'data-element': 'industries-grid'
      }
    },
    ...industryCardElements
  ];

  return {
    industriesSection: newElements[0],
    elements: newElements
  };
}; 